// pages/TermsConditions.jsx
import React, {useState, useEffect, useLayoutEffect} from 'react';
import { motion } from 'framer-motion';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';

const TermsConditions = () => {
    const [isScrolled, setIsScrolled] = useState(false);


    useLayoutEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 50);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const sections = [
        {
            title: "Acceptance of Terms",
            content: [
                "By accessing and using KROMAIC's website, products, or services, you agree to be bound by these Terms and Conditions and our Privacy Policy.",
                "If you do not agree with any part of these terms, please discontinue use of our services immediately.",
                "We reserve the right to modify these terms at any time. Continued use after changes constitutes acceptance."
            ]
        },
        {
            title: "Eligibility & Account",
            content: [
                "You must be at least 18 years old to create an account or make purchases on KROMAIC.",
                "You are responsible for maintaining the confidentiality of your account credentials and for all activities under your account.",
                "We reserve the right to refuse service, terminate accounts, or cancel orders at our discretion."
            ]
        },
        {
            title: "Product Information",
            content: [
                "We strive to accurately display product colors and details, but actual colors may vary due to device display differences.",
                "Product descriptions are provided for informational purposes and do not constitute medical advice.",
                "All skincare products are for external use only. Perform a patch test before full application."
            ]
        },
        {
            title: "Pricing & Payment",
            content: [
                "All prices are in USD unless otherwise stated. We reserve the right to adjust prices at any time.",
                "Payment is processed through secure, PCI-compliant payment gateways.",
                "Sales tax and import duties are calculated at checkout based on your shipping destination."
            ]
        },
        {
            title: "Shipping & Delivery",
            content: [
                "Orders are processed within 1-2 business days. Delivery times vary by location.",
                "We are not responsible for delays caused by customs, weather, or carrier issues.",
                "Risk of loss transfers to you upon delivery confirmation by the carrier."
            ]
        },
        {
            title: "Intellectual Property",
            content: [
                "All content on this site, including text, graphics, logos, and images, is the property of KROMAIC and protected by international copyright laws.",
                "You may not reproduce, distribute, or create derivative works without our express written permission.",
                "KROMAIC® and associated logos are registered trademarks."
            ]
        },
        {
            title: "Limitation of Liability",
            content: [
                "KROMAIC's liability shall not exceed the purchase price of the product in question.",
                "We are not liable for incidental or consequential damages arising from product use.",
                "These limitations apply to the fullest extent permitted by law."
            ]
        },
        {
            title: "Governing Law",
            content: [
                "These Terms are governed by the laws of the State of New York, without regard to conflict of law principles.",
                "Any disputes shall be resolved in the courts of New York County, New York.",
                "International customers acknowledge that their local consumer laws may provide additional protections."
            ]
        }
    ];

    return (
        <div className="min-h-screen bg-pearl-white">
            {/* Fixed Navbar - EXACTLY like Shop page */}
            <div
                className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                    isScrolled
                        ? 'bg-white/95 backdrop-blur-sm shadow-soft'
                        : 'bg-transparent'
                }`}
            >
                <Navbar />
            </div>

            {/* Hero Section - Emotional headline with soft background */}
            <section id="hero" className="pt-20">
                <div className="relative min-h-[40vh] flex items-center bg-gradient-to-br from-soft-ivory via-pearl-white to-nude-beige overflow-hidden">
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1 }}
                        className="container-luxury relative z-10"
                    >
                        <div className="max-w-4xl">
                            <h1 className="font-playfair text-5xl md:text-6xl lg:text-7xl text-deep-charcoal mb-6 leading-tight">
                                Terms & Conditions
                            </h1>
                            <p className="font-poppins text-xl text-charcoal-60 max-w-2xl">
                                Clarity and transparency in every interaction
                            </p>
                        </div>
                    </motion.div>

                    {/* Decorative elements */}
                    <div className="absolute top-0 right-0 w-1/3 h-full opacity-5">
                        <div className="absolute top-1/4 right-0 w-64 h-64 rounded-full bg-champagne-gold blur-3xl" />
                    </div>
                </div>
            </section>

            {/* Content Section - Using section-spacing pattern */}
            <section id="terms-content" className="section-spacing">
                <div className="container-luxury">
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        className="max-w-4xl mx-auto"
                    >
                        {/* Updated Date */}
                        <div className="mb-12 text-center">
                            <motion.p
                                initial={{ opacity: 0 }}
                                whileInView={{ opacity: 1 }}
                                viewport={{ once: true }}
                                transition={{ duration: 1 }}
                                className="font-poppins text-charcoal-60 italic"
                            >
                                Last Updated: December 1, 2023
                            </motion.p>
                        </div>

                        {/* Terms Content */}
                        <div className="bg-white rounded-2xl shadow-soft p-8 md:p-12">
                            {sections.map((section, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 40 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, amount: 0.3 }}
                                    transition={{ duration: 0.6, delay: index * 0.1 }}
                                    className={`py-8 ${index < sections.length - 1 ? 'border-b border-nude-beige' : ''}`}
                                >
                                    <h2 className="font-cormorant text-2xl md:text-3xl text-deep-charcoal mb-6">
                                        {section.title}
                                    </h2>
                                    <div className="space-y-4">
                                        {section.content.map((paragraph, pIndex) => (
                                            <motion.p
                                                key={pIndex}
                                                initial={{ opacity: 0 }}
                                                whileInView={{ opacity: 1 }}
                                                viewport={{ once: true }}
                                                transition={{ duration: 0.5, delay: index * 0.1 + pIndex * 0.1 }}
                                                className="font-poppins text-charcoal-60 leading-relaxed"
                                            >
                                                {paragraph}
                                            </motion.p>
                                        ))}
                                    </div>
                                </motion.div>
                            ))}
                        </div>

                        {/* Contact Section */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 1, delay: 0.2 }}
                            className="mt-16"
                        >
                            <div className="bg-gradient-to-r from-muted-sage/5 to-champagne-gold/5 rounded-2xl p-8 md:p-10">
                                <h3 className="font-cormorant text-2xl md:text-3xl text-deep-charcoal mb-6">
                                    Contact for Terms Inquiries
                                </h3>
                                <p className="font-poppins text-charcoal-60 mb-8 max-w-2xl">
                                    For questions about our Terms & Conditions or legal matters:
                                </p>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <motion.a
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        href="mailto:legal@kromaic.com"
                                        className="px-6 py-4 bg-white border border-nude-beige text-deep-charcoal font-poppins rounded-lg
                      hover:border-muted-sage hover:text-muted-sage transition-all duration-300 text-center
                      shadow-soft hover:shadow-medium"
                                    >
                                        legal@kromaic.com
                                    </motion.a>
                                    <motion.a
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        href="tel:+18885551234"
                                        className="px-6 py-4 bg-deep-charcoal text-white font-poppins rounded-lg
                      hover:bg-deep-charcoal/90 transition-all duration-300 text-center
                      shadow-soft hover:shadow-medium"
                                    >
                                        +1 (888) 555-1234
                                    </motion.a>
                                </div>

                                <motion.div
                                    initial={{ opacity: 0 }}
                                    whileInView={{ opacity: 1 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.8, delay: 0.4 }}
                                    className="mt-8 pt-6 border-t border-nude-beige"
                                >
                                    <p className="font-poppins text-sm text-charcoal-60 text-center">
                                        Our legal team typically responds within 24-48 hours
                                    </p>
                                </motion.div>
                            </div>
                        </motion.div>

                        {/* Back to Top */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.6 }}
                            className="mt-12 text-center"
                        >
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                                className="px-6 py-3 bg-transparent border border-nude-beige text-deep-charcoal
                  font-poppins rounded-full hover:border-muted-sage hover:text-muted-sage
                  transition-all duration-300 flex items-center gap-2 mx-auto"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                </svg>
                                Back to Top
                            </motion.button>
                        </motion.div>
                    </motion.div>
                </div>
            </section>

            {/* Trust Badges Section - Similar to newsletter in shop */}
            <section id="trust" className="section-spacing bg-nude-beige/30">
                <div className="container-luxury">
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                        className="max-w-4xl mx-auto text-center"
                    >
                        <h3 className="font-cormorant text-3xl text-deep-charcoal mb-8">
                            Trust & Transparency
                        </h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                            {[
                                { label: "Secure Transactions", icon: "🔒" },
                                { label: "Privacy First", icon: "👁️" },
                                { label: "Ethical Standards", icon: "⭐" },
                                { label: "Global Compliance", icon: "🌍" }
                            ].map((badge, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    whileInView={{ opacity: 1, scale: 1 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.5, delay: index * 0.1 }}
                                    className="bg-white p-6 rounded-xl shadow-soft"
                                >
                                    <div className="text-3xl mb-3">{badge.icon}</div>
                                    <p className="font-poppins text-sm text-charcoal-60">{badge.label}</p>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default TermsConditions;